import Ajv from "ajv";

export const MARKETING_SCHEMA_VERSION = 1;

export const MARKETING_FORMATS = {
  BANNER: "banner",
  POPUP: "popup",
  VIDEO: "video",
  ROULETTE: "roulette",
};

export const MARKETING_ASSET_TYPES = {
  IMAGE: "image",
  VIDEO: "video",
};

export const MARKETING_PLACEMENTS = {
  HEADER_TOP: "header_top",
  AFTER_HEADER: "after_header",
  AFTER_BOXES: "after_boxes",
  BETWEEN_SUGGESTED_ARTISTS: "between_suggested_artists",
  SEARCH_TOP: "search_top",
  SONG_LIST_INLINE: "song_list_inline",
  FLOATING_ABOVE_PLAYER: "floating_above_player",
};

export const DEFAULT_MARKETING_FREQUENCY = {
  scope: "session",
  maxImpressions: 1,
  cooldownSeconds: 900,
};

export const DEFAULT_MARKETING_TARGET = {
  openIn: "new_tab",
};

export const DEFAULT_MARKETING_CONFIG = {
  schemaVersion: MARKETING_SCHEMA_VERSION,
  enabled: false,
  revision: "default",
  defaults: {
    frequency: DEFAULT_MARKETING_FREQUENCY,
    target: DEFAULT_MARKETING_TARGET,
  },
  campaigns: [],
  errors: [],
};

export const DEFAULT_ROULETTE_PRIZES = [
  { angle: 0, label: "5 créditos" },
  { angle: 45, label: "VIP por 3 días" },
  { angle: 90, label: "2 créditos" },
  { angle: 135, label: "2X puntos por 1 hora" },
  { angle: 180, label: "100 puntos" },
  { angle: 225, label: "50 puntos" },
  { angle: 270, label: "25 puntos" },
  { angle: 315, label: "10 créditos" },
];

const TRIGGER_TYPES = [
  "page_view",
  "time_on_page",
  "idle",
  "scroll_depth",
  "slot_visible",
  "event",
];

const FREQUENCY_SCOPES = ["session", "user_day", "user", "radio_day"];
const TARGET_OPEN_IN = ["new_tab", "same_tab"];
const DEVICES = ["mobile", "desktop"];

const marketingSchema = {
  type: "object",
  additionalProperties: true,
  properties: {
    schemaVersion: { type: "number" },
    enabled: { type: "boolean" },
    revision: { type: "string" },
    defaults: {
      type: "object",
      additionalProperties: true,
      properties: {
        frequency: {
          type: "object",
          additionalProperties: true,
          properties: {
            scope: { enum: FREQUENCY_SCOPES },
            maxImpressions: { type: "number" },
            cooldownSeconds: { type: "number" },
          },
        },
        target: {
          type: "object",
          additionalProperties: true,
          properties: {
            href: { type: "string" },
            openIn: { enum: TARGET_OPEN_IN },
          },
        },
      },
    },
    campaigns: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: true,
        properties: {
          id: { type: "string" },
          enabled: { type: "boolean" },
          priority: { type: "number" },
          validFrom: { type: "string" },
          validTo: { type: "string" },
          devices: {
            type: "array",
            items: { enum: DEVICES },
          },
          triggers: {
            type: "array",
            items: {
              type: "object",
              additionalProperties: true,
              properties: {
                type: { enum: TRIGGER_TYPES },
                name: { type: "string" },
                delaySeconds: { type: "number" },
                afterSeconds: { type: "number" },
                percentage: { type: "number" },
                placement: { type: "string" },
              },
            },
          },
          frequency: {
            type: "object",
            additionalProperties: true,
            properties: {
              scope: { enum: FREQUENCY_SCOPES },
              maxImpressions: { type: "number" },
              cooldownSeconds: { type: "number" },
            },
          },
          target: {
            type: "object",
            additionalProperties: true,
            properties: {
              href: { type: "string" },
              openIn: { enum: TARGET_OPEN_IN },
            },
          },
          creatives: {
            type: "array",
            items: {
              type: "object",
              additionalProperties: true,
              properties: {
                id: { type: "string" },
                enabled: { type: "boolean" },
                priority: { type: "number" },
                format: { enum: Object.values(MARKETING_FORMATS) },
                placements: {
                  type: "array",
                  items: { type: "string" },
                },
                asset: {
                  type: "object",
                  additionalProperties: true,
                  properties: {
                    type: { enum: Object.values(MARKETING_ASSET_TYPES) },
                    src: { type: "string" },
                    desktopSrc: { type: "string" },
                    discSrc: { type: "string" },
                    poster: { type: "string" },
                    alt: { type: "string" },
                  },
                },
                target: {
                  type: "object",
                  additionalProperties: true,
                  properties: {
                    href: { type: "string" },
                    openIn: { enum: TARGET_OPEN_IN },
                  },
                },
                triggers: {
                  type: "array",
                  items: {
                    type: "object",
                    additionalProperties: true,
                    properties: {
                      type: { enum: TRIGGER_TYPES },
                      name: { type: "string" },
                      delaySeconds: { type: "number" },
                      afterSeconds: { type: "number" },
                      percentage: { type: "number" },
                      placement: { type: "string" },
                    },
                  },
                },
                frequency: {
                  type: "object",
                  additionalProperties: true,
                  properties: {
                    scope: { enum: FREQUENCY_SCOPES },
                    maxImpressions: { type: "number" },
                    cooldownSeconds: { type: "number" },
                  },
                },
                title: { type: "string" },
                body: { type: "string" },
                ctaLabel: { type: "string" },
                dismissible: { type: "boolean" },
                stopAfterClick: { type: "boolean" },
                stopAfterClickSeconds: { type: "number" },
                showCloseAfterSeconds: { type: "number" },
                skipAfterSeconds: { type: "number" },
                closeOnEnd: { type: "boolean" },
                autoplay: { type: "boolean" },
                muted: { type: "boolean" },
                playsInline: { type: "boolean" },
                controls: { type: "boolean" },
                prizes: {
                  type: "array",
                  items: {
                    type: "object",
                    additionalProperties: true,
                    properties: {
                      angle: { type: "number" },
                      label: { type: "string" },
                    },
                  },
                },
                spinDurationMs: { type: "number" },
                inlineEvery: { type: "number" },
                floatingOffsetPx: { type: "number" },
                headerBottomOffsetPx: { type: "number" },
              },
            },
          },
        },
      },
    },
  },
};

const ajv = new Ajv({ allErrors: true });
const validateMarketingSchema = ajv.compile(marketingSchema);

const isPlainObject = (value) =>
  value !== null && typeof value === "object" && !Array.isArray(value);

const toDisabledConfig = (revision = "default", errors = []) => ({
  ...DEFAULT_MARKETING_CONFIG,
  revision,
  defaults: {
    frequency: { ...DEFAULT_MARKETING_FREQUENCY },
    target: { ...DEFAULT_MARKETING_TARGET },
  },
  campaigns: [],
  errors,
});

const parseMaybeJson = (value) => {
  if (typeof value !== "string") return value;

  const trimmed = value.trim();
  if (!trimmed) return null;

  try {
    return JSON.parse(trimmed);
  } catch (error) {
    try {
      return JSON.parse(trimmed.replaceAll("'", '"'));
    } catch (_error) {
      return null;
    }
  }
};

const normalizePlainText = (value) => {
  if (typeof value !== "string") return null;

  const normalized = value.replace(/<[^>]*>/g, "").replace(/\s+/g, " ").trim();
  return normalized === "" ? null : normalized;
};

const normalizeNumber = (value, fallback, { min = null, max = null } = {}) => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;

  if (min !== null && parsed < min) return fallback;
  if (max !== null && parsed > max) return fallback;

  return parsed;
};

const normalizeDate = (value) => {
  if (typeof value !== "string" || value.trim() === "") return null;

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;

  return value;
};

export const isAllowedMarketingUrl = (value) => {
  if (typeof value !== "string") return false;

  const trimmed = value.trim();
  if (!trimmed) return false;

  if (trimmed.startsWith("/") && !trimmed.startsWith("//") && !trimmed.startsWith("/\\")) {
    return true;
  }

  try {
    return new URL(trimmed).protocol === "https:";
  } catch (_error) {
    return false;
  }
};

const normalizeUrl = (value) => {
  if (!isAllowedMarketingUrl(value)) return null;
  return value.trim();
};

const normalizeTarget = (target, fallback = DEFAULT_MARKETING_TARGET) => {
  const candidate = isPlainObject(target) ? target : {};
  const href = normalizeUrl(candidate.href ?? fallback?.href ?? null);
  const openIn = TARGET_OPEN_IN.includes(candidate.openIn)
    ? candidate.openIn
    : TARGET_OPEN_IN.includes(fallback?.openIn)
      ? fallback.openIn
      : DEFAULT_MARKETING_TARGET.openIn;

  return {
    ...(href ? { href } : {}),
    openIn,
  };
};

const normalizeFrequency = (frequency, fallback = DEFAULT_MARKETING_FREQUENCY) => {
  const candidate = isPlainObject(frequency) ? frequency : {};
  const fallbackFrequency = isPlainObject(fallback)
    ? fallback
    : DEFAULT_MARKETING_FREQUENCY;
  const scope = FREQUENCY_SCOPES.includes(candidate.scope)
    ? candidate.scope
    : FREQUENCY_SCOPES.includes(fallbackFrequency.scope)
      ? fallbackFrequency.scope
      : DEFAULT_MARKETING_FREQUENCY.scope;

  return {
    scope,
    maxImpressions: normalizeNumber(
      candidate.maxImpressions,
      normalizeNumber(
        fallbackFrequency.maxImpressions,
        DEFAULT_MARKETING_FREQUENCY.maxImpressions,
        { min: 1 }
      ),
      { min: 1 }
    ),
    cooldownSeconds: normalizeNumber(
      candidate.cooldownSeconds,
      normalizeNumber(
        fallbackFrequency.cooldownSeconds,
        DEFAULT_MARKETING_FREQUENCY.cooldownSeconds,
        { min: 0 }
      ),
      { min: 0 }
    ),
  };
};

const normalizeTrigger = (trigger) => {
  if (!isPlainObject(trigger) || !TRIGGER_TYPES.includes(trigger.type)) {
    return null;
  }

  const normalized = {
    type: trigger.type,
    delaySeconds: normalizeNumber(trigger.delaySeconds, 0, { min: 0 }),
  };

  if (trigger.type === "event") {
    const name = normalizePlainText(trigger.name);
    if (!name) return null;
    normalized.name = name;
  }

  if (trigger.type === "idle" || trigger.type === "time_on_page") {
    normalized.afterSeconds = normalizeNumber(
      trigger.afterSeconds,
      normalizeNumber(trigger.delaySeconds, 0, { min: 0 }),
      { min: 0 }
    );
  }

  if (trigger.type === "scroll_depth") {
    normalized.percentage = normalizeNumber(trigger.percentage, 50, {
      min: 1,
      max: 100,
    });
  }

  if (trigger.type === "slot_visible") {
    const placement = normalizePlainText(trigger.placement);
    if (placement) normalized.placement = placement;
  }

  return normalized;
};

const normalizeTriggers = (triggers) => {
  if (!Array.isArray(triggers)) return [];

  return triggers.map(normalizeTrigger).filter(Boolean);
};

const normalizePlacements = (placements, fallback = [MARKETING_PLACEMENTS.AFTER_HEADER]) => {
  if (!Array.isArray(placements) || placements.length === 0) {
    return fallback;
  }

  const normalized = placements
    .map(normalizePlainText)
    .filter((placement) => Object.values(MARKETING_PLACEMENTS).includes(placement));

  return normalized.length > 0 ? Array.from(new Set(normalized)) : fallback;
};

const normalizeAsset = (asset, expectedType) => {
  if (!isPlainObject(asset)) return null;

  const type = asset.type ?? expectedType;
  if (type !== expectedType) return null;

  const src = normalizeUrl(asset.src);
  if (!src) return null;

  const desktopSrc = normalizeUrl(asset.desktopSrc);
  const discSrc = normalizeUrl(asset.discSrc);
  const poster = normalizeUrl(asset.poster);

  return {
    type,
    src,
    ...(desktopSrc ? { desktopSrc } : {}),
    ...(discSrc ? { discSrc } : {}),
    ...(poster ? { poster } : {}),
    alt: normalizePlainText(asset.alt) ?? "",
  };
};

const getExpectedAssetType = (format) =>
  format === MARKETING_FORMATS.VIDEO
    ? MARKETING_ASSET_TYPES.VIDEO
    : MARKETING_ASSET_TYPES.IMAGE;

const normalizeDevices = (devices) => {
  if (!Array.isArray(devices) || devices.length === 0) {
    return [...DEVICES];
  }

  const normalized = devices.filter((device) => DEVICES.includes(device));
  return normalized.length > 0 ? Array.from(new Set(normalized)) : [...DEVICES];
};

const normalizeRoulettePrizes = (prizes) => {
  const normalized = Array.isArray(prizes)
    ? prizes
        .map((prize) => {
          if (!isPlainObject(prize)) return null;

          const angle = normalizeNumber(prize.angle, null, {
            min: 0,
            max: 359,
          });
          const label = normalizePlainText(prize.label);

          if (angle === null || !label) return null;

          return { angle, label };
        })
        .filter(Boolean)
    : [];

  if (normalized.length === 0) {
    return [...DEFAULT_ROULETTE_PRIZES];
  }

  const seenAngles = new Set();
  return normalized.filter((prize) => {
    if (seenAngles.has(prize.angle)) return false;
    seenAngles.add(prize.angle);
    return true;
  });
};

const normalizeCreative = (creative, campaign, defaults) => {
  if (!isPlainObject(creative) || creative.enabled === false) return null;

  const id = normalizePlainText(creative.id);
  const format = creative.format;
  if (!id || !Object.values(MARKETING_FORMATS).includes(format)) return null;

  const asset = normalizeAsset(creative.asset, getExpectedAssetType(format));
  if (!asset) return null;
  if (format === MARKETING_FORMATS.ROULETTE && !asset.discSrc) {
    return null;
  }

  const frequency = normalizeFrequency(
    creative.frequency,
    campaign.frequency ?? defaults.frequency
  );
  const target = normalizeTarget(
    creative.target,
    campaign.target ?? defaults.target
  );

  const normalized = {
    id,
    enabled: true,
    priority: normalizeNumber(creative.priority, campaign.priority ?? 0),
    format,
    asset,
    target,
    frequency,
    triggers: normalizeTriggers(creative.triggers),
    dismissible: creative.dismissible !== false,
    stopAfterClick: creative.stopAfterClick === true,
    stopAfterClickSeconds: normalizeNumber(
      creative.stopAfterClickSeconds,
      null,
      { min: 1 }
    ),
    title: normalizePlainText(creative.title),
    body: normalizePlainText(creative.body),
    ctaLabel: normalizePlainText(creative.ctaLabel),
    showCloseAfterSeconds: normalizeNumber(creative.showCloseAfterSeconds, 0, {
      min: 0,
    }),
    skipAfterSeconds: normalizeNumber(creative.skipAfterSeconds, 0, { min: 0 }),
    closeOnEnd: creative.closeOnEnd === true,
    autoplay: creative.autoplay !== false,
    muted: creative.muted !== false,
    playsInline: creative.playsInline !== false,
    controls: creative.controls === true,
  };

  if (format === MARKETING_FORMATS.ROULETTE) {
    normalized.prizes = normalizeRoulettePrizes(creative.prizes);
    normalized.spinDurationMs = normalizeNumber(creative.spinDurationMs, 3600, {
      min: 500,
    });
  }

  if (format === MARKETING_FORMATS.BANNER) {
    normalized.placements = normalizePlacements(creative.placements);
    normalized.inlineEvery = normalizeNumber(creative.inlineEvery, null, {
      min: 1,
    });
    normalized.floatingOffsetPx = normalizeNumber(
      creative.floatingOffsetPx,
      null,
      { min: 0 }
    );
    normalized.headerBottomOffsetPx = normalizeNumber(
      creative.headerBottomOffsetPx,
      null,
      { min: 0 }
    );
  }

  return normalized;
};

const normalizeCampaign = (campaign, defaults) => {
  if (!isPlainObject(campaign) || campaign.enabled === false) return null;

  const id = normalizePlainText(campaign.id);
  if (!id) return null;

  const normalizedCampaign = {
    id,
    enabled: true,
    priority: normalizeNumber(campaign.priority, 0),
    validFrom: normalizeDate(campaign.validFrom),
    validTo: normalizeDate(campaign.validTo),
    devices: normalizeDevices(campaign.devices),
    triggers: normalizeTriggers(campaign.triggers),
    frequency: normalizeFrequency(campaign.frequency, defaults.frequency),
    target: normalizeTarget(campaign.target, defaults.target),
    creatives: [],
  };

  normalizedCampaign.creatives = Array.isArray(campaign.creatives)
    ? campaign.creatives
        .map((creative) => normalizeCreative(creative, normalizedCampaign, defaults))
        .filter(Boolean)
    : [];

  if (normalizedCampaign.creatives.length === 0) return null;

  return normalizedCampaign;
};

const getSchemaErrors = () =>
  (validateMarketingSchema.errors ?? []).map((error) => {
    const path = error.instancePath || error.dataPath || "marketing";
    return `${path} ${error.message}`.trim();
  });

export const normalizeMarketingConfig = (input) => {
  const parsed = parseMaybeJson(input);
  const candidate =
    isPlainObject(parsed?.marketing) && !Array.isArray(parsed.campaigns)
      ? parsed.marketing
      : parsed;

  if (!isPlainObject(candidate)) {
    return toDisabledConfig();
  }

  const revision = normalizePlainText(candidate.revision) ?? "default";

  if (candidate.enabled === false) {
    return toDisabledConfig(revision);
  }

  const isValid = validateMarketingSchema(candidate);
  if (!isValid) {
    return toDisabledConfig(revision, getSchemaErrors());
  }

  const defaults = {
    frequency: normalizeFrequency(candidate.defaults?.frequency),
    target: normalizeTarget(candidate.defaults?.target),
  };

  const campaigns = Array.isArray(candidate.campaigns)
    ? candidate.campaigns
        .map((campaign) => normalizeCampaign(campaign, defaults))
        .filter(Boolean)
        .sort((a, b) => b.priority - a.priority)
    : [];

  return {
    schemaVersion: normalizeNumber(
      candidate.schemaVersion,
      MARKETING_SCHEMA_VERSION,
      { min: 1 }
    ),
    enabled: candidate.enabled !== false && campaigns.length > 0,
    revision,
    defaults,
    campaigns,
    errors: [],
  };
};

export const isMarketingConfigEnabled = (config) =>
  !!config && config.enabled === true && Array.isArray(config.campaigns);
